import { runCompanyCapexSlice, type CompanyCapexSliceResult } from "./models/companyCapex";
import { runComputeDeploymentSlice, type ComputeDeploymentSliceResult } from "./models/computeDeployment";
import { runFundingPressureSlice, type FundingPressureSliceResult } from "./models/fundingPressure";
import { runLabArrSlice, type LabArrSliceResult } from "./models/labArr";
import { runPowerDemandSlice, type PowerDemandSliceResult } from "./models/powerDemand";
import { runTokenDemandSlice, type TokenDemandSliceResult } from "./models/tokenDemand";
import { runTokenVolumeSlice, type TokenVolumeSliceResult } from "./models/tokenVolume";
import tokenArrEvidenceJson from "./data/token-arr-evidence.json";
import aiMarketEvidenceJson from "./data/ai-market-evidence.json";
import computeRentalEvidenceJson from "./data/compute-rental-evidence.json";
import { parseAiMarketPanel } from "./dataSources/aiMarket";
import { parseComputeRentalPanel } from "./dataSources/computeRental";
import { mergePlatformEvidence, parseTokenArrEvidence } from "./dataSources/tokenArrEvidence";
import type { ComputeRentalPanel, DataPanel } from "./types";

const TOKEN_ARR_EVIDENCE = parseTokenArrEvidence(tokenArrEvidenceJson);
const AI_MARKET_EVIDENCE = parseAiMarketPanel(aiMarketEvidenceJson);
const COMPUTE_RENTAL_EVIDENCE = parseComputeRentalPanel(computeRentalEvidenceJson);

export interface ResearchScenario {
  capexIntensity: number;
  tokenAnnualDemandGrowthPct: number;
  tokenUnitPriceChangePct: number;
  tokenPriceElasticity: number;
  tokenAiPenetrationMultiplier: number;
  tokenAgentSharePct: number;
  tokenTaskLengthMultiplier: number;
  tokenComputeCapacityMultiplier: number;
  arrCodingPaidUserMultiplier: number;
  arrB2bApiAnnualGrowthPct: number;
  arrEnterpriseSeatMultiplier: number;
  arrEffectivePriceMultiplier: number;
  arrComputeCapacityMultiplier: number;
  fundingAnnualCapexGrowthDeltaPct: number;
  fundingAnnualOcfGrowthDeltaPct: number;
  powerDeploymentLagQuarters: number;
  powerUtilization: number;
  powerPue: number;
}

export const DEFAULT_RESEARCH_SCENARIO: ResearchScenario = {
  capexIntensity: 1,
  tokenAnnualDemandGrowthPct: 0,
  tokenUnitPriceChangePct: 0,
  tokenPriceElasticity: 1,
  tokenAiPenetrationMultiplier: 1,
  tokenAgentSharePct: 90.17232228562946,
  tokenTaskLengthMultiplier: 1,
  tokenComputeCapacityMultiplier: 1,
  arrCodingPaidUserMultiplier: 1,
  arrB2bApiAnnualGrowthPct: 0,
  arrEnterpriseSeatMultiplier: 1,
  arrEffectivePriceMultiplier: 1,
  arrComputeCapacityMultiplier: 1,
  fundingAnnualCapexGrowthDeltaPct: 0,
  fundingAnnualOcfGrowthDeltaPct: 0,
  powerDeploymentLagQuarters: 1,
  powerUtilization: 0.7,
  powerPue: 1.25,
};

export interface ResearchModelBundle {
  schemaVersion: 2;
  asOf: string;
  generatedAt: string;
  scenario: ResearchScenario;
  companyCapex: CompanyCapexSliceResult;
  tokenDemand: TokenDemandSliceResult | null;
  tokenVolume: TokenVolumeSliceResult;
  labArr: LabArrSliceResult;
  rentalMarket: ComputeRentalPanel;
  computeDeployment: ComputeDeploymentSliceResult | null;
  fundingPressure: FundingPressureSliceResult;
  powerDemand: PowerDemandSliceResult | null;
  availability: {
    tokenDemand: string;
    tokenVolume: string;
    labArr: string;
    rentalMarket: string;
    computeDeployment: string;
    powerDemand: string;
  };
}

/**
 * One deterministic orchestration seam for the page and Worker publication.
 * Optional normalized inputs remain explicit unavailable slices; core SEC
 * company models fail loudly if their point-in-time contract is broken.
 */
export function runResearchModels(
  panel: DataPanel,
  input: Partial<ResearchScenario> = {},
): ResearchModelBundle {
  const scenario = { ...DEFAULT_RESEARCH_SCENARIO, ...input };
  for (const [key, value] of Object.entries(scenario)) {
    if (!Number.isFinite(value)) throw new Error(`Research scenario ${key} must be finite.`);
  }

  const companyCapex = runCompanyCapexSlice(panel, { capexIntensity: scenario.capexIntensity });
  const fundingPressure = runFundingPressureSlice(panel, {
    benchmark: panel.cashFlowBenchmark,
    scenario: {
      annualCapexGrowthDeltaPct: scenario.fundingAnnualCapexGrowthDeltaPct,
      annualOcfGrowthDeltaPct: scenario.fundingAnnualOcfGrowthDeltaPct,
    },
  });
  const tokenDemand = panel.monthly?.length
    ? runTokenDemandSlice(panel.monthly, {
        asOf: panel.meta.asOf,
        scenario: {
          demandGrowthPct: scenario.tokenAnnualDemandGrowthPct,
          unitPriceChangePct: scenario.tokenUnitPriceChangePct,
          priceElasticity: scenario.tokenPriceElasticity,
        },
      })
    : null;
  const platformMonthly = mergePlatformEvidence(panel.monthly ?? [], TOKEN_ARR_EVIDENCE.platformMonthly, panel.meta.asOf);
  const tokenVolume = runTokenVolumeSlice({
    asOf: panel.meta.asOf,
    platformMonthly,
    aiMarket: panel.aiMarket ? parseAiMarketPanel(panel.aiMarket) : AI_MARKET_EVIDENCE,
    tasks: TOKEN_ARR_EVIDENCE.tokenTasks,
    scenario: {
      aiPenetrationMultiplier: scenario.tokenAiPenetrationMultiplier,
      agentSharePct: scenario.tokenAgentSharePct,
      taskLengthMultiplier: scenario.tokenTaskLengthMultiplier,
      computeCapacityMultiplier: scenario.tokenComputeCapacityMultiplier,
      annualDemandGrowthPct: scenario.tokenAnnualDemandGrowthPct,
      unitPriceChangePct: scenario.tokenUnitPriceChangePct,
      priceElasticity: scenario.tokenPriceElasticity,
    },
  });
  const labArr = runLabArrSlice({
    asOf: panel.meta.asOf,
    latestCompleteMonth: tokenVolume.monitor.latestCompleteMonth,
    anchors: TOKEN_ARR_EVIDENCE.arrAnchors,
    platformMonthly,
    segments: TOKEN_ARR_EVIDENCE.arrSegments,
    externalBenchmarks: TOKEN_ARR_EVIDENCE.arrExternalBenchmarks,
    scenario: {
      codingPaidUserMultiplier: scenario.arrCodingPaidUserMultiplier,
      b2bApiAnnualGrowthPct: scenario.arrB2bApiAnnualGrowthPct,
      enterpriseSeatMultiplier: scenario.arrEnterpriseSeatMultiplier,
      effectivePriceMultiplier: scenario.arrEffectivePriceMultiplier,
      computeCapacityMultiplier: scenario.arrComputeCapacityMultiplier,
    },
  });
  const rentalMarket = panel.rentalMarket ? parseComputeRentalPanel(panel.rentalMarket) : COMPUTE_RENTAL_EVIDENCE;
  const computeDeployment = panel.chipSales?.length
    ? runComputeDeploymentSlice(panel.chipSales, { asOf: panel.meta.asOf })
    : null;
  const powerDemand = computeDeployment && panel.epochPower
    ? runPowerDemandSlice({ compute: computeDeployment, epochPower: panel.epochPower }, {
        scenario: {
          deploymentLagQuarters: scenario.powerDeploymentLagQuarters,
          utilization: scenario.powerUtilization,
          pue: scenario.powerPue,
        },
      })
    : null;

  return {
    schemaVersion: 2,
    asOf: panel.meta.asOf,
    generatedAt: panel.meta.generatedAt,
    scenario,
    companyCapex,
    tokenDemand,
    tokenVolume,
    labArr,
    rentalMarket,
    computeDeployment,
    fundingPressure,
    powerDemand,
    availability: {
      tokenDemand: tokenDemand ? "available" : "unavailable: normalized monthly demand history is missing",
      tokenVolume: "available: platform backtest plus assumption-banded structural estimate",
      labArr: "available: sparse private-company milestone backtest; research-baseline",
      rentalMarket: panel.rentalMarket
        ? "available: daily Worker snapshot from Vast, RunPod, Azure, and getflops history"
        : "available: bundled PII-stripped snapshot; waiting for the next Worker refresh",
      computeDeployment: computeDeployment ? "available" : "unavailable: normalized chip-sales observations are missing",
      powerDemand: powerDemand ? "available" : "unavailable: compute deployment and parsed Epoch power inputs are both required",
    },
  };
}
